import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener, ViewChild, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import * as Hammer from 'hammerjs';
import { LogEntryPhoto } from '../../../../../models/logs/photos/LogEntryPhoto.interface';
import { PhotoMetadata } from '../../../../../models/logs/photos/PhotoMetadata';
import { AssetsDataService } from '../../../../../service/assets-data.service';

interface PhotoType {
  id: string | number;
  url: string;
  name?: string;
  blobName?: string;
}

@Component({
  selector: 'app-photo-viewer-modal',
  templateUrl: './photo-viewer-modal.component.html',
  styleUrls: ['./photo-viewer-modal.component.css']
})
export class PhotoViewerModalComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('photoViewer', { static: false }) photoViewer!: ElementRef;
  @ViewChild('photoImage', { static: false }) photoImage!: ElementRef;
  private hammer: HammerManager | null = null;

  @Input() isOpen: boolean = false;
  @Input() photoList: PhotoType[] = [];
  @Input() initialIndex: number = 0;
  @Output() closeModal = new EventEmitter<void>();
  
  public currentPhotoIndex: number = 0;
  public selectedPhoto: PhotoType | null = null;

  // Zoom and pan properties
  public scale: number = 1;
  public posX: number = 0;
  public posY: number = 0;
  public startScale: number = 1;
  public startPosX: number = 0;
  public startPosY: number = 0;

  // Rotation in degrees (0/90/180/270); per photo, cleared on navigation.
  public rotation: number = 0;

  // Mouse wheel zoom properties
  public zoomSpeed: number = 0.1;
  public minScale: number = 1;
  public maxScale: number = 3;



  constructor(private _assetsDataService: AssetsDataService) {}

  ngOnInit(): void {

    this.currentPhotoIndex = this.initialIndex;
    this.updateSelectedPhoto();
  }

  ngAfterViewInit() {
    this.setupHammerGestures();
    if (this.photoList.length > 0) {
      this.currentPhotoIndex = this.initialIndex;
      this.selectedPhoto = this.photoList[this.currentPhotoIndex];
    }
  }
  
  ngOnDestroy() {
    if (this.hammer) {
      this.hammer.destroy();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] && this.isOpen) || changes['photoList'] || changes['initialIndex']) {
      this.setInitialPhoto();
    }
  }

  private setInitialPhoto(): void {
    this.currentPhotoIndex = this.initialIndex;
    
    if (this.photoList && this.photoList.length > 0) {
      // Ensure index is valid
      if (this.currentPhotoIndex >= this.photoList.length) {
        this.currentPhotoIndex = 0;
      }
      this.selectedPhoto = this.photoList[this.currentPhotoIndex];
      this.resetZoom();
    } else {
      this.selectedPhoto = null;
    }
  }

  /**
   * Navigate to previous or next photo
   */
  navigatePhoto(direction: number): void {
    const newIndex = this.currentPhotoIndex + direction;
    if (newIndex >= 0 && newIndex < this.photoList.length) {
      this.currentPhotoIndex = newIndex;
      this.selectedPhoto = this.photoList[this.currentPhotoIndex];
      this.resetZoom();
    }
  }

  onCloseModal(): void {
    this.closeModal.emit();
  }

  /**
   * Set up hammer.js gestures for pinch zoom and panning
   */
  private setupHammerGestures(): void {
    if (!this.photoViewer || !this.photoViewer.nativeElement) return;

    this.hammer = new Hammer.Manager(this.photoViewer.nativeElement);
    
    // Add recognizers
    const pinch = new Hammer.Pinch();
    const pan = new Hammer.Pan();
    
    // Add the recognizers
    this.hammer.add([pinch, pan]);

    // Handle pinch
    this.hammer.on('pinchstart', () => {
      this.startScale = this.scale;
    });

    this.hammer.on('pinch', (e) => {
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.startScale * e.scale));
      if (newScale !== this.scale) {
        this.scale = newScale;
      }
    });

    // Handle pan
    this.hammer.on('panstart', () => {
      this.startPosX = this.posX;
      this.startPosY = this.posY;
    });

    this.hammer.on('pan', (e) => {
      if (this.scale > 1) {
        this.posX = this.startPosX + e.deltaX / this.scale;
        this.posY = this.startPosY + e.deltaY / this.scale;
      }
    });
  }

  /**
   * Handler for mouse wheel zoom
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (this.isOpen && this.selectedPhoto) {
      event.preventDefault();
      
      // Determine zoom direction and amount
      const delta = event.deltaY > 0 ? -this.zoomSpeed : this.zoomSpeed;
      
      // Calculate new scale with boundaries
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));
      
      // Only apply if the scale actually changed
      if (newScale !== this.scale) {
        // Get mouse position relative to the photo viewer
        const rect = this.photoViewer.nativeElement.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        // Calculate the center point of the image
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        // Calculate how far the mouse is from the center
        const distX = (mouseX - centerX) / this.scale;
        const distY = (mouseY - centerY) / this.scale;
        
        // Calculate the offset based on the new scale
        const offsetX = distX * (1 - newScale / this.scale);
        const offsetY = distY * (1 - newScale / this.scale);
        
        // Apply the new scale and position
        this.scale = newScale;
        this.posX += offsetX;
        this.posY += offsetY;
        this.startScale = this.scale;
      }
    }
  }

  /**
   * The image's CSS transform: zoom/pan first (screen-aligned), rotation
   * applied innermost so panning keeps following the mouse/finger direction.
   */
  public get imageTransform(): string {
    const parts: string[] = [];
    if (this.scale > 1) {
      parts.push(`scale(${this.scale}) translate(${this.posX}px, ${this.posY}px)`);
    }
    if (this.rotation !== 0) {
      parts.push(`rotate(${this.rotation}deg)`);
    }
    return parts.join(' ');
  }

  /**
   * Rotate the photo 90° clockwise (view-only — the stored photo is untouched)
   */
  rotatePhoto(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  /**
   * Reset zoom, position, and rotation
   */
  resetZoom(): void {
    this.scale = 1;
    this.posX = 0;
    this.posY = 0;
    this.startScale = 1;
    this.startPosX = 0;
    this.startPosY = 0;
    this.rotation = 0;
  }

  /**
   * Single click to toggle between default zoom and a higher zoom level
   */
  toggleZoom(event: MouseEvent): void {
    if (this.scale === 1) {
      this.zoomIn();
    } else {
      this.resetZoom();
    }
  }

  /**
   * Zoom in by a fixed amount
   */
  zoomIn(): void {
    if (this.scale < this.maxScale) {
      this.scale += 0.5;
    }
  }

  /**
   * Zoom out by a fixed amount
   */
  zoomOut(): void {
    if (this.scale > this.minScale) {
      this.scale -= 0.5;
    }
  }

  /**
   * Handle keyboard navigation
   */
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isOpen && this.selectedPhoto) {
      if (event.key === 'ArrowLeft') {
        this.navigatePhoto(-1);
      } else if (event.key === 'ArrowRight') {
        this.navigatePhoto(1);
      } else if (event.key === 'Escape') {
        this.onCloseModal();
      } else if (event.key === '+' || event.key === '=') {
        this.zoomIn();
      } else if (event.key === '-') {
        this.zoomOut();
      } else if (event.key === '0') {
        this.resetZoom();
      } else if (event.key === 'r' || event.key === 'R') {
        this.rotatePhoto();
      }
    }
  }

  private updateSelectedPhoto(): void {
    if (this.currentPhotoIndex >= 0 && this.currentPhotoIndex < this.photoList.length) {
      this.selectedPhoto = this.photoList[this.currentPhotoIndex];
    }
  }
}